import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useGetTenantQuery,
  useGetAssignedSolutionsQuery,
  useGetAvailableSolutionsQuery,
  useAssignSolutionMutation,
  useGetUnitsQuery,
  useCreateUnitMutation,
  useUpdateUnitStatusMutation,
  useUpdateUnitDbConfigMutation,
  useGetUnitUserQuery,
  useAssignUserToUnitMutation,
  useUnassignUserFromUnitMutation,
  useLazySearchUnitsByCognitoQuery,
  useLazySearchUnitsByHierarchyQuery,
  useGetUnitMaintainersQuery,
  useAssignMaintainerToUnitMutation,
  useUnassignMaintainerFromUnitMutation,
  useGetMaintainersQuery,
} from '../store/apiSlice';
import { useNotification } from '../contexts/NotificationContext';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import './TenantDetail.css';

const TenantDetail: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const { tenantId: encodedTenantId } = useParams<{ tenantId: string }>();
  // Decode the tenant ID from URL (it was encoded to handle special characters like #)
  const tenantId = encodedTenantId ? decodeURIComponent(encodedTenantId) : '';

  const { data: tenantRes, isLoading: loadingTenant, error: tenantError } = useGetTenantQuery(tenantId, { skip: !tenantId });
  const { data: solutionsRes, isLoading: loadingSolutions, error: solutionsError } = useGetAssignedSolutionsQuery(tenantId, { skip: !tenantId });
  const { data: availableSolutionsRes, error: availableSolutionsError } = useGetAvailableSolutionsQuery();
  const [assignSolution, { isLoading: assigning }] = useAssignSolutionMutation();

  const [showSolutionForm, setShowSolutionForm] = useState(false);
  const [selectedSolutionCode, setSelectedSolutionCode] = useState('');

  useEffect(() => {
    if (tenantError) {
      showError(`Failed to load tenant details: ${(tenantError as any).message || 'Unknown error'}`);
    }
  }, [tenantError, showError]);

  useEffect(() => {
    if (solutionsError) {
      showError(`Failed to load solutions: ${(solutionsError as any).message || 'Unknown error'}`);
    }
  }, [solutionsError, showError]);

  useEffect(() => {
    if (availableSolutionsError) {
      console.warn('Failed to load available solutions:', availableSolutionsError);
    }
  }, [availableSolutionsError]);

  const tenant = tenantRes?.data || null;
  const solutions = solutionsRes?.data || [];
  const availableSolutions = availableSolutionsRes?.data || [];
  const loading = loadingTenant || loadingSolutions || assigning;

  const handleAssignSolution = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !selectedSolutionCode.trim()) return;

    try {
      await assignSolution({ tenantId, solutionCode: selectedSolutionCode }).unwrap();
      setSelectedSolutionCode('');
      setShowSolutionForm(false);
      showSuccess('Solution assigned successfully');
    } catch (error: any) {
      console.error('Error assigning solution:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to assign solution';
      showError(errorMessage);
    }
  };

  // Get assigned solution codes
  const assignedSolutionCodes = new Set(solutions.map((s: any) => s.solutionCode || '').filter(Boolean));

  // Filter available solutions - show only unassigned ones
  const unassignedSolutions = availableSolutions.filter(
    (sol) => !assignedSolutionCodes.has(sol.code)
  );

  return (
    <Layout>
      <div className="tenant-detail">
        <Link to="/tenants" className="back-link">← Back to Tenants</Link>
      
      <header className="tenant-header">
        <h1>{tenant?.name || (loading ? 'Loading...' : 'Tenant')}</h1>
        <p>ID: {tenantId}</p>
      </header>

      <section className="solutions-section">
        <div className="section-header">
          <h2>Solutions</h2>
          {!loading && availableSolutions.length > 0 && unassignedSolutions.length > 0 && (
            <button onClick={() => setShowSolutionForm(!showSolutionForm)} className="create-button">
              Assign Solution
            </button>
          )}
        </div>

        {showSolutionForm && (
          <div className="create-form">
            <form onSubmit={handleAssignSolution}>
              {unassignedSolutions.length === 0 ? (
                <p style={{ padding: '10px', color: '#666' }}>
                  All available solutions have been assigned to this tenant.
                </p>
              ) : (
                <>
                  <select
                    value={selectedSolutionCode}
                    onChange={(e) => setSelectedSolutionCode(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
                  >
                    <option value="">Select a solution...</option>
                    {unassignedSolutions.map((sol) => (
                      <option key={sol.code} value={sol.code}>
                        {sol.name} ({sol.code})
                      </option>
                    ))}
                  </select>
                  <div className="form-actions">
                    <button type="submit" disabled={loading || !selectedSolutionCode}>
                      {loading ? 'Assigning...' : 'Assign'}
                    </button>
                    <button type="button" onClick={() => setShowSolutionForm(false)}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        )}
        
        <div className="solutions-list">
          {loadingTenant || loadingSolutions ? (
            <div className="loading-indicator">Loading solutions...</div>
          ) : availableSolutions.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
              <p>No predefined solutions available. Please contact your administrator.</p>
              <p style={{ fontSize: '12px', marginTop: '10px', color: '#999' }}>
                (Check browser console for API errors)
              </p>
            </div>
          ) : solutions.length === 0 ? (
            <div>
              <p>No solutions assigned yet.</p>
              {unassignedSolutions.length === 0 && availableSolutions.length > 0 && (
                <p style={{ marginTop: '10px', color: '#666' }}>All available solutions have been assigned.</p>
              )}
            </div>
          ) : (
            solutions.map((solution, index) => (
              <SolutionCard 
                key={solution.solutionId || `solution-${index}-${solution.name}`} 
                solution={solution} 
                tenantId={tenantId!} 
              />
            ))
          )}
        </div>
      </section>
      </div>
    </Layout>
  );
};

const SolutionCard: React.FC<{ solution: any; tenantId: string }> = ({ solution, tenantId }) => {
  const { showSuccess, showError } = useNotification();
  
  const { data: unitsRes, isLoading: loadingUnits } = useGetUnitsQuery({ tenantId, solutionId: solution.solutionId });
  const [createUnit, { isLoading: creatingUnit }] = useCreateUnitMutation();
  const [triggerSearchCognito, { isFetching: searchingCognito }] = useLazySearchUnitsByCognitoQuery();
  const [triggerSearchHierarchy, { isFetching: searchingHierarchy }] = useLazySearchUnitsByHierarchyQuery();

  const [filteredUnits, setFilteredUnits] = useState<any[] | null>(null);
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'cognito' | 'hierarchy'>('name');

  const units = unitsRes?.data || [];
  const displayUnits = filteredUnits !== null ? filteredUnits : units;
  const loading = loadingUnits || creatingUnit || searchingCognito || searchingHierarchy;

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setFilteredUnits(null);
      return;
    }

    try {
      let searchResults: any[] = [];

      if (searchType === 'cognito') {
        // Search by Cognito username - uses GSI1 for rapid lookup
        const response = await triggerSearchCognito(searchQuery.trim()).unwrap();
        searchResults = response.data || [];
        // Filter to only show units from this solution
        searchResults = searchResults.filter(
          (u: any) => u.solutionId === solution.solutionId && u.tenantId === tenantId
        );
      } else if (searchType === 'hierarchy') {
        // Search by hierarchy pattern (tenant-solution-unit)
        const response = await triggerSearchHierarchy(searchQuery.trim()).unwrap();
        searchResults = response.data || [];
        // Filter to only show units from this solution
        searchResults = searchResults.filter(
          (u: any) => u.solutionId === solution.solutionId && u.tenantId === tenantId
        );
      } else {
        // Search by name (local filter)
        searchResults = units.filter((u: any) =>
          u.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setFilteredUnits(searchResults);
      if (searchResults.length === 0) {
        showError('No units found matching the search criteria');
      }
    } catch (error: any) {
      console.error('Error searching units:', error);
      showError(error.data?.message || error.message || 'Failed to search units');
      setFilteredUnits(null);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredUnits(null);
  };

  const handleCreateUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUnitName.trim()) return;

    try {
      await createUnit({ tenantId, solutionId: solution.solutionId, name: newUnitName.trim() }).unwrap();
      setNewUnitName('');
      setShowUnitForm(false);
      setFilteredUnits(null); // Clear search to show the new unit
      showSuccess('Unit created successfully');
    } catch (error: any) {
      console.error('Error creating unit:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to create unit';
      showError(errorMessage);
    }
  };

  return (
    <div className="solution-card">
      <h3>{solution.name}</h3>
      <p>ID: {solution.solutionId}</p>

      <div className="units-section">
        <div className="units-header">
          <h4>Units</h4>
          <button onClick={() => setShowUnitForm(!showUnitForm)} className="small-button">
            + Unit
          </button>
        </div>

        <div className="units-search">
          <div className="search-controls">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value as 'name' | 'cognito' | 'hierarchy')}
              className="search-type-select"
            >
              <option value="name">Search by Name</option>
              <option value="cognito">Search by Cognito Username</option>
              <option value="hierarchy">Search by Hierarchy</option>
            </select>
            <input
              type="text"
              placeholder={
                searchType === 'cognito'
                  ? 'Enter Cognito username...'
                  : searchType === 'hierarchy'
                  ? 'Enter hierarchy (tenant-solution-unit)...'
                  : 'Search units by name...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button" disabled={loading}>
              Search
            </button>
            {searchQuery && (
              <button onClick={handleClearSearch} className="clear-search-button">
                Clear
              </button>
            )}
          </div>
        </div>

        {showUnitForm && (
          <form onSubmit={handleCreateUnit} className="unit-form">
            <input
              type="text"
              placeholder="Unit Name"
              value={newUnitName}
              onChange={(e) => setNewUnitName(e.target.value)}
              required
              disabled={loading}
            />
            <div className="form-actions">
              <button type="submit" disabled={loading}>
                {loading ? 'Creating...' : 'Create'}
              </button>
              <button type="button" onClick={() => setShowUnitForm(false)} disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <ul className="units-list">
          {displayUnits.length === 0 ? (
            <li className="no-units">No units found</li>
          ) : (
            displayUnits.map((unit, index) => (
              <UnitItem
                key={unit.unitId || `unit-${index}-${unit.name}`}
                unit={unit}
                tenantId={tenantId}
                solutionId={solution.solutionId}
              />
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

const UnitItem: React.FC<{ unit: any; tenantId: string; solutionId: string }> = ({
  unit,
  tenantId,
  solutionId,
}) => {
  const { showSuccess, showError, showWarning } = useNotification();
  const [showUserForm, setShowUserForm] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [disabled, setDisabled] = useState(unit.disabled || false);
  const [formData, setFormData] = useState({
    cognitoUsername: '',
    email: '',
    displayName: '',
    password: '',
    permissionLevel: 'Novice',
  });

  // Maintainer states
  const [showMaintainers, setShowMaintainers] = useState(false);
  const [showMaintainerForm, setShowMaintainerForm] = useState(false);
  const [maintainerForm, setMaintainerForm] = useState({
    cognitoUsername: '',
    permissionLevel: 'Novice',
  });

  // DB queries and mutations
  const { data: userRes, isLoading: loadingUser } = useGetUnitUserQuery(
    { tenantId, solutionId, unitId: unit.unitId },
    { skip: !showUser }
  );
  const [assignUser, { isLoading: assigningUser }] = useAssignUserToUnitMutation();
  const [unassignUser, { isLoading: unassigningUser }] = useUnassignUserFromUnitMutation();
  const [updateDbConfig, { isLoading: dbConfigSaving }] = useUpdateUnitDbConfigMutation();
  const [updateStatus, { isLoading: statusUpdating }] = useUpdateUnitStatusMutation();

  // Maintainer queries and mutations
  const { data: unitMaintainersRes, isLoading: loadingUnitMaintainers } = useGetUnitMaintainersQuery(
    { tenantId, solutionId, unitId: unit.unitId },
    { skip: !showMaintainers }
  );
  const unitMaintainers = unitMaintainersRes?.data || [];

  const { data: globalMaintainersRes } = useGetMaintainersQuery();
  const globalMaintainers = globalMaintainersRes?.data || [];

  const [assignMaintainer, { isLoading: assigningMaintainer }] = useAssignMaintainerToUnitMutation();
  const [unassignMaintainer, { isLoading: unassigningMaintainer }] = useUnassignMaintainerFromUnitMutation();

  const [assignedUser, setAssignedUser] = useState<any>(null);

  // Database configuration state
  const [showDbConfig, setShowDbConfig] = useState(false);
  const [useIntegratedDb, setUseIntegratedDb] = useState(
    unit.useIntegratedDatabase !== undefined ? unit.useIntegratedDatabase : true
  );
  const [dbForm, setDbForm] = useState({
    dbHost: unit.dbHost || '',
    dbPort: unit.dbPort || 5432,
    dbDatabase: unit.dbDatabase || '',
    dbSchema: unit.dbSchema || 'public',
    dbUsername: unit.dbUsername || '',
    dbPassword: '',
    dbConnectionTimeout: unit.dbConnectionTimeout || 30,
    dbSslMode: unit.dbSslMode || 'disable',
    dbSslRootCert: '',
    dbSslCertificate: '',
    dbSslKey: '',
  });
  const [showAdvancedSsl, setShowAdvancedSsl] = useState(false);

  const loading = loadingUser || assigningUser || unassigningUser || statusUpdating;

  // Sync assignedUser and other unit state when unit props or query data changes
  useEffect(() => {
    if (unit.cognitoUsername) {
      setAssignedUser({
        cognitoUsername: unit.cognitoUsername,
        email: unit.email,
        displayName: unit.displayName,
        permissionLevel: unit.permissionLevel,
      });
    } else if (userRes) {
      setAssignedUser(userRes.data || null);
    } else {
      setAssignedUser(null);
    }
    setDisabled(unit.disabled || false);
    setUseIntegratedDb(unit.useIntegratedDatabase !== undefined ? unit.useIntegratedDatabase : true);
    setDbForm((prev) => ({
      ...prev,
      dbHost: unit.dbHost || '',
      dbPort: unit.dbPort || 5432,
      dbDatabase: unit.dbDatabase || '',
      dbSchema: unit.dbSchema || 'public',
      dbUsername: unit.dbUsername || '',
      dbConnectionTimeout: unit.dbConnectionTimeout || 30,
      dbSslMode: unit.dbSslMode || 'disable',
    }));
  }, [unit, showUser, userRes]);

  const handleAssignUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.cognitoUsername.trim() || !formData.email.trim()) {
      showWarning('Cognito username and email are required');
      return;
    }

    try {
      await assignUser({
        tenantId,
        solutionId,
        unitId: unit.unitId,
        cognitoUsername: formData.cognitoUsername,
        email: formData.email,
        displayName: formData.displayName || undefined,
        password: formData.password || undefined,
        permissionLevel: formData.permissionLevel,
      }).unwrap();
      setFormData({ cognitoUsername: '', email: '', displayName: '', password: '', permissionLevel: 'Novice' });
      setShowUserForm(false);
      showSuccess('User assigned to unit successfully');
    } catch (error: any) {
      console.error('Error assigning user:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to assign user to unit';
      showError(errorMessage);
    }
  };

  const handleUnassignUserClick = () => {
    setShowConfirmDialog(true);
  };

  const handleUnassignUser = async () => {
    setShowConfirmDialog(false);
    try {
      await unassignUser({ tenantId, solutionId, unitId: unit.unitId }).unwrap();
      showSuccess('User unassigned from unit successfully');
    } catch (error: any) {
      console.error('Error unassigning user:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to unassign user from unit';
      showError(errorMessage);
    }
  };

  const handleAssignMaintainer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!maintainerForm.cognitoUsername) {
      showWarning('Please select a maintainer');
      return;
    }
    try {
      await assignMaintainer({
        tenantId,
        solutionId,
        unitId: unit.unitId,
        cognitoUsername: maintainerForm.cognitoUsername,
        permissionLevel: maintainerForm.permissionLevel,
      }).unwrap();
      setMaintainerForm({ cognitoUsername: '', permissionLevel: 'Novice' });
      setShowMaintainerForm(false);
      showSuccess('Maintainer assigned to unit successfully');
    } catch (err: any) {
      console.error('Error assigning maintainer:', err);
      showError(err.data?.message || err.message || 'Failed to assign maintainer');
    }
  };

  const handleUnassignMaintainer = async (username: string) => {
    try {
      await unassignMaintainer({
        tenantId,
        solutionId,
        unitId: unit.unitId,
        username,
      }).unwrap();
      showSuccess('Maintainer unassigned from unit successfully');
    } catch (err: any) {
      console.error('Error unassigning maintainer:', err);
      showError(err.data?.message || err.message || 'Failed to unassign maintainer');
    }
  };

  const handleSaveDbConfig = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    try {
      const payload: any = { useIntegratedDatabase: useIntegratedDb };
      if (!useIntegratedDb) {
        payload.dbHost = dbForm.dbHost;
        payload.dbPort = dbForm.dbPort;
        payload.dbDatabase = dbForm.dbDatabase;
        payload.dbSchema = dbForm.dbSchema;
        payload.dbUsername = dbForm.dbUsername;
        payload.dbConnectionTimeout = dbForm.dbConnectionTimeout;
        payload.dbSslMode = dbForm.dbSslMode;
        if (dbForm.dbPassword) payload.dbPassword = dbForm.dbPassword;
        if (dbForm.dbSslRootCert) payload.dbSslRootCert = dbForm.dbSslRootCert;
        if (dbForm.dbSslCertificate) payload.dbSslCertificate = dbForm.dbSslCertificate;
        if (dbForm.dbSslKey) payload.dbSslKey = dbForm.dbSslKey;
      }
      await updateDbConfig({ tenantId, solutionId, unitId: unit.unitId, payload }).unwrap();
      setDbForm(prev => ({ ...prev, dbPassword: '', dbSslRootCert: '', dbSslCertificate: '', dbSslKey: '' }));
      showSuccess('Database configuration saved successfully');
    } catch (error: any) {
      console.error('Error saving db config:', error);
      showError(error.data?.message || error.message || 'Failed to save database configuration');
    }
  };

  const handleToggleIntegratedDb = async (checked: boolean) => {
    setUseIntegratedDb(checked);
    if (checked) {
      try {
        await updateDbConfig({
          tenantId,
          solutionId,
          unitId: unit.unitId,
          payload: { useIntegratedDatabase: true },
        }).unwrap();
        showSuccess('Switched to integrated database');
      } catch (error: any) {
        console.error('Error switching to integrated database:', error);
        showError(error.data?.message || error.message || 'Failed to switch to integrated database');
        setUseIntegratedDb(false); // revert on error
      }
    }
  };

  const handleToggleDisabled = async () => {
    try {
      const newDisabledStatus = !disabled;
      await updateStatus({
        tenantId,
        solutionId,
        unitId: unit.unitId,
        disabled: newDisabledStatus,
      }).unwrap();
      setDisabled(newDisabledStatus);
      showSuccess(`Unit ${newDisabledStatus ? 'disabled' : 'enabled'} successfully`);
    } catch (error: any) {
      console.error('Error updating unit status:', error);
      const errorMessage = error.data?.message || error.message || 'Failed to update unit status';
      showError(errorMessage);
    }
  };

  return (
    <li className={`unit-item ${disabled ? 'unit-disabled' : ''}`}>
      <div className="unit-header">
        <span className="unit-name">
          {unit.name}
          {disabled && <span className="disabled-badge"> (Disabled)</span>}
        </span>
        <div className="unit-actions">
          <button
            onClick={handleToggleDisabled}
            className={`toggle-status-button ${disabled ? 'enable-button' : 'disable-button'}`}
            title={disabled ? 'Enable unit' : 'Disable unit'}
            disabled={loading}
          >
            {disabled ? '✓ Enable' : '✗ Disable'}
          </button>
          <button
            onClick={() => setShowDbConfig(!showDbConfig)}
            className="db-config-toggle-button"
            title="Toggle database configuration"
          >
            {showDbConfig ? '▼' : '▶'} DB Config
          </button>
          <button
            onClick={() => setShowUser(!showUser)}
            className="user-toggle-button"
            title="Toggle user info"
          >
            {showUser ? '▼' : '▶'} User
          </button>
          <button
            onClick={() => setShowMaintainers(!showMaintainers)}
            className="maintainers-toggle-button"
            title="Toggle unit maintainers list"
            style={{
              padding: '6px 12px',
              borderRadius: '4px',
              border: '1px solid #17a2b8',
              backgroundColor: '#17a2b8',
              color: 'white',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              marginLeft: '8px'
            }}
          >
            {showMaintainers ? '▼' : '▶'} Maintainers
          </button>
          {assignedUser ? (
            <button
              onClick={handleUnassignUserClick}
              className="unassign-user-button"
              title="Unassign user"
              disabled={loading}
            >
              Unassign
            </button>
          ) : (
            <button
              onClick={() => setShowUserForm(!showUserForm)}
              className="assign-user-button"
              title="Assign user"
            >
              + User
            </button>
          )}
        </div>
      </div>

      {showUserForm && (
        <form onSubmit={handleAssignUser} className="user-form">
          <input
            type="text"
            placeholder="Cognito Username (e.g., user@example.com)"
            value={formData.cognitoUsername}
            onChange={(e) => setFormData({ ...formData, cognitoUsername: e.target.value })}
            required
            disabled={loading}
          />
          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            disabled={loading}
          />
          <input
            type="text"
            placeholder="Display Name (optional)"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Password (optional - for new users)"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            disabled={loading}
          />
          <div className="form-group" style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>Permission Level</label>
            <select
              value={formData.permissionLevel}
              onChange={(e) => setFormData({ ...formData, permissionLevel: e.target.value })}
              disabled={loading}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="Novice">Novice</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
              <option value="Master">Master</option>
            </select>
          </div>
          <div className="form-actions">
            <button type="submit" disabled={loading}>
              {loading ? 'Assigning...' : 'Assign'}
            </button>
            <button type="button" onClick={() => setShowUserForm(false)} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {showUser && (
        <div className="user-info">
          {loadingUser ? (
            <p className="no-user">Loading user info...</p>
          ) : assignedUser ? (
            <div className="user-item">
              <div>
                <strong>{assignedUser.displayName || assignedUser.cognitoUsername}</strong>
                <span className="user-email">{assignedUser.email}</span>
                <div style={{ marginTop: '8px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <strong style={{ color: '#555' }}>Permission Level:</strong>
                  <select
                    value={assignedUser.permissionLevel || 'Novice'}
                    onChange={async (e) => {
                      try {
                        const newLevel = e.target.value;
                        await assignUser({
                          tenantId,
                          solutionId,
                          unitId: unit.unitId,
                          cognitoUsername: assignedUser.cognitoUsername,
                          email: assignedUser.email,
                          displayName: assignedUser.displayName || undefined,
                          permissionLevel: newLevel,
                        }).unwrap();
                        setAssignedUser({ ...assignedUser, permissionLevel: newLevel });
                        showSuccess(`Permission level updated to ${newLevel}`);
                      } catch (err: any) {
                        showError(err.data?.message || err.message || 'Failed to update permission level');
                      }
                    }}
                    disabled={loading}
                    style={{ padding: '2px 6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                  >
                    <option value="Novice">Novice</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Expert">Expert</option>
                    <option value="Master">Master</option>
                  </select>
                </div>
              </div>
              <span className="user-username">{assignedUser.cognitoUsername}</span>
            </div>
          ) : (
            <p className="no-user">No user assigned to this unit</p>
          )}
        </div>
      )}

      {showDbConfig && (
        <div className="db-config-section">
          <div className="db-toggle-row">
            <label className="db-toggle-label">
              <span>Use Integrated Database</span>
              <input
                type="checkbox"
                checked={useIntegratedDb}
                onChange={(e) => handleToggleIntegratedDb(e.target.checked)}
                disabled={dbConfigSaving}
              />
            </label>
          </div>

          {!useIntegratedDb && (
            <form onSubmit={handleSaveDbConfig} className="db-config-form">
              <div className="db-form-group">
                <label>Host *</label>
                <input type="text" value={dbForm.dbHost}
                  onChange={(e) => setDbForm({ ...dbForm, dbHost: e.target.value })}
                  placeholder="e.g., db.example.com" required disabled={dbConfigSaving} />
              </div>
              <div className="db-form-row">
                <div className="db-form-group">
                  <label>Port</label>
                  <input type="number" value={dbForm.dbPort}
                    onChange={(e) => setDbForm({ ...dbForm, dbPort: parseInt(e.target.value) || 5432 })} disabled={dbConfigSaving} />
                </div>
                <div className="db-form-group">
                  <label>Database *</label>
                  <input type="text" value={dbForm.dbDatabase}
                    onChange={(e) => setDbForm({ ...dbForm, dbDatabase: e.target.value })}
                    required disabled={dbConfigSaving} />
                </div>
              </div>
              <div className="db-form-row">
                <div className="db-form-group">
                  <label>Schema</label>
                  <input type="text" value={dbForm.dbSchema}
                    onChange={(e) => setDbForm({ ...dbForm, dbSchema: e.target.value })} disabled={dbConfigSaving} />
                </div>
                <div className="db-form-group">
                  <label>Connection Timeout (s)</label>
                  <input type="number" value={dbForm.dbConnectionTimeout}
                    onChange={(e) => setDbForm({ ...dbForm, dbConnectionTimeout: parseInt(e.target.value) || 30 })} disabled={dbConfigSaving} />
                </div>
              </div>
              <div className="db-form-row">
                <div className="db-form-group">
                  <label>Username *</label>
                  <input type="text" value={dbForm.dbUsername}
                    onChange={(e) => setDbForm({ ...dbForm, dbUsername: e.target.value })}
                    required disabled={dbConfigSaving} />
                </div>
                <div className="db-form-group">
                  <label>Password * {unit.hasDbPassword && <span className="secret-indicator">(saved)</span>}</label>
                  <input type="password" value={dbForm.dbPassword}
                    onChange={(e) => setDbForm({ ...dbForm, dbPassword: e.target.value })}
                    placeholder={unit.hasDbPassword ? '(leave blank to keep existing)' : 'Required'}
                    required={!unit.hasDbPassword} disabled={dbConfigSaving} />
                </div>
              </div>

              <div className="db-form-group">
                <label>SSL Mode</label>
                <select value={dbForm.dbSslMode}
                  onChange={(e) => setDbForm({ ...dbForm, dbSslMode: e.target.value })} disabled={dbConfigSaving}>
                  <option value="disable">disable</option>
                  <option value="require">require</option>
                  <option value="verify-ca">verify-ca</option>
                  <option value="verify-full">verify-full</option>
                </select>
              </div>

              {dbForm.dbSslMode !== 'disable' && (
                <>
                  <div className="db-form-group">
                    <label>SSL Root Certificate {unit.hasSslRootCert && <span className="secret-indicator">(saved)</span>}</label>
                    <textarea value={dbForm.dbSslRootCert}
                      onChange={(e) => setDbForm({ ...dbForm, dbSslRootCert: e.target.value })}
                      placeholder="Paste PEM certificate..." rows={3} disabled={dbConfigSaving} />
                  </div>

                  <button type="button" className="advanced-ssl-toggle"
                    onClick={() => setShowAdvancedSsl(!showAdvancedSsl)} disabled={dbConfigSaving}>
                    {showAdvancedSsl ? '▼' : '▶'} Client Certificate (Advanced)
                  </button>

                  {showAdvancedSsl && (
                    <>
                      <div className="db-form-group">
                        <label>SSL Client Certificate {unit.hasSslCertificate && <span className="secret-indicator">(saved)</span>}</label>
                        <textarea value={dbForm.dbSslCertificate}
                          onChange={(e) => setDbForm({ ...dbForm, dbSslCertificate: e.target.value })}
                          placeholder="Paste client certificate PEM..." rows={3} disabled={dbConfigSaving} />
                      </div>
                      <div className="db-form-group">
                        <label>SSL Client Key {unit.hasSslKey && <span className="secret-indicator">(saved)</span>}</label>
                        <textarea value={dbForm.dbSslKey}
                          onChange={(e) => setDbForm({ ...dbForm, dbSslKey: e.target.value })}
                          placeholder="Paste client key PEM..." rows={3} disabled={dbConfigSaving} />
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="form-actions">
                <button type="submit" disabled={dbConfigSaving}>
                  {dbConfigSaving ? 'Saving...' : 'Save DB Config'}
                </button>
              </div>
            </form>
          )}

        </div>
      )}

      {showMaintainers && (
        <div className="maintainers-section-unit" style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '6px', border: '1px solid #eee' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h5 style={{ margin: 0, fontSize: '14px', color: '#333' }}>Unit Maintainers</h5>
            <button
              onClick={() => setShowMaintainerForm(!showMaintainerForm)}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #28a745',
                backgroundColor: '#28a745',
                color: 'white',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              + Maintainer
            </button>
          </div>

          {showMaintainerForm && (
            <form onSubmit={handleAssignMaintainer} style={{ marginBottom: '15px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', backgroundColor: '#fff', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Select Maintainer *</label>
                <select
                  value={maintainerForm.cognitoUsername}
                  onChange={(e) => setMaintainerForm({ ...maintainerForm, cognitoUsername: e.target.value })}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px' }}
                  required
                >
                  <option value="">Choose...</option>
                  {globalMaintainers.map((m) => (
                    <option key={m.cognitoUsername} value={m.cognitoUsername}>
                      {m.displayName ? `${m.displayName} (${m.cognitoUsername})` : m.cognitoUsername}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: '#555', marginBottom: '4px' }}>Permission Level *</label>
                <select
                  value={maintainerForm.permissionLevel}
                  onChange={(e) => setMaintainerForm({ ...maintainerForm, permissionLevel: e.target.value })}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px' }}
                  required
                >
                  <option value="Novice">Novice</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Expert">Expert</option>
                  <option value="Master">Master</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '5px' }}>
                <button type="submit" disabled={assigningMaintainer} style={{ padding: '6px 12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                  {assigningMaintainer ? 'Assigning...' : 'Assign'}
                </button>
                <button type="button" onClick={() => setShowMaintainerForm(false)} style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {loadingUnitMaintainers ? (
            <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>Loading unit maintainers...</p>
          ) : unitMaintainers.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>No maintainers assigned to this unit yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {unitMaintainers.map((m: any) => (
                <div key={m.cognitoUsername} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #eee' }}>
                  <div>
                    <strong style={{ fontSize: '13px' }}>{m.displayName || m.cognitoUsername}</strong>
                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>{m.email}</span>
                    <span style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      borderRadius: '12px',
                      backgroundColor: '#e2f0d9',
                      color: '#385723',
                      fontWeight: 'bold',
                      marginLeft: '10px'
                    }}>
                      {m.permissionLevel}
                    </span>
                  </div>
                  <button
                    onClick={() => handleUnassignMaintainer(m.cognitoUsername)}
                    disabled={unassigningMaintainer}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: '1px solid #dc3545',
                      backgroundColor: 'transparent',
                      color: '#dc3545',
                      cursor: 'pointer',
                      fontSize: '11px'
                    }}
                  >
                    Unassign
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Unassign User"
        message="Are you sure you want to unassign this user from the unit?"
        confirmText="Unassign"
        cancelText="Cancel"
        type="danger"
        onConfirm={handleUnassignUser}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </li>
  );
};

export default TenantDetail;

